import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { FileText, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { apiService } from '../../services/api';

interface ChunkHighlight {
  content: string;
  chunkId: number;
  similarity: number;
  page: number;
  section: string;
}

interface DocumentViewerPanelProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: string;
  documentName: string;
  highlightedChunks?: ChunkHighlight[];
  className?: string;
}

const DocumentViewerPanel: React.FC<DocumentViewerPanelProps> = ({
  isOpen,
  onClose,
  documentId,
  documentName,
  highlightedChunks = [],
  className = '',
}) => {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentHighlight, setCurrentHighlight] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);
  const highlightRefs = useRef<(HTMLSpanElement | null)[]>([]);

  useEffect(() => {
    if (isOpen && documentId) {
      loadDocumentContent();
    }
  }, [isOpen, documentId]);

  useEffect(() => {
    // Auto-scroll to first highlighted chunk when content loads
    if (content && highlightedChunks.length > 0) {
      setTimeout(() => scrollToHighlight(0), 100);
    }
  }, [content, highlightedChunks]);

  const loadDocumentContent = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await apiService.getDocumentContent(documentId);
      
      if (result.success && result.content) {
        setContent(result.content);
      } else {
        setError(result.error || 'Failed to load document content');
      }
    } catch (err) {
      setError('An error occurred while loading the document');
      console.error('Document loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  const scrollToHighlight = (index: number) => {
    if (highlightRefs.current[index]) {
      highlightRefs.current[index]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      setCurrentHighlight(index);
    }
  };

  const renderContentWithHighlights = () => {
    if (!content || highlightedChunks.length === 0) {
      return (
        <div className="whitespace-pre-wrap text-sm text-gray-800 leading-relaxed">
          {content}
        </div>
      );
    }

    console.log('🔍 DEBUG: Highlighting chunks:', highlightedChunks.length);
    console.log('🔍 DEBUG: Document content length:', content.length);

    let renderedContent = content;
    const highlights: Array<{ start: number; end: number; index: number; chunk: ChunkHighlight }> = [];

    // Find all chunk positions in the content
    highlightedChunks.forEach((chunk, index) => {
      const chunkText = chunk.content.trim();
      console.log(`🔍 DEBUG: Looking for chunk ${index}:`, chunkText.substring(0, 100) + '...');
      
      // Try multiple matching strategies
      let startIndex = -1;
      let endIndex = -1;
      
      // Strategy 1: Exact match
      startIndex = renderedContent.indexOf(chunkText);
      if (startIndex !== -1) {
        endIndex = startIndex + chunkText.length;
        console.log(`✅ Exact match found at ${startIndex}-${endIndex}`);
      }
      
      // Strategy 2: Partial match (first 150 chars)
      if (startIndex === -1 && chunkText.length > 150) {
        const shortText = chunkText.substring(0, 150).trim();
        startIndex = renderedContent.indexOf(shortText);
        if (startIndex !== -1) {
          // Try to find the end
          const endPart = chunkText.substring(Math.max(0, chunkText.length - 100)).trim();
          const tempEndIndex = renderedContent.indexOf(endPart, startIndex);
          if (tempEndIndex !== -1) {
            endIndex = tempEndIndex + endPart.length;
            console.log(`✅ Partial match found at ${startIndex}-${endIndex}`);
          } else {
            // Just use a reasonable chunk size
            endIndex = Math.min(startIndex + 500, renderedContent.length);
            console.log(`⚠️ Partial match with estimated end at ${startIndex}-${endIndex}`);
          }
        }
      }
      
      // Strategy 3: Word-based fuzzy matching
      if (startIndex === -1) {
        const chunkWords = chunkText.split(/\s+/).slice(0, 10); // First 10 words
        const searchText = chunkWords.join(' ');
        startIndex = renderedContent.indexOf(searchText);
        if (startIndex !== -1) {
          endIndex = Math.min(startIndex + Math.max(300, chunkText.length), renderedContent.length);
          console.log(`✅ Word-based match found at ${startIndex}-${endIndex}`);
        }
      }
      
      if (startIndex !== -1 && endIndex !== -1) {
        highlights.push({
          start: startIndex,
          end: endIndex,
          index,
          chunk
        });
      } else {
        console.log(`❌ No match found for chunk ${index}`);
      }
    });

    // Sort highlights by position
    highlights.sort((a, b) => a.start - b.start);
    
    console.log(`🎯 DEBUG: Found ${highlights.length} highlights out of ${highlightedChunks.length} chunks`);

    // Fallback: If no highlights found, create fake ones for testing
    if (highlights.length === 0 && highlightedChunks.length > 0) {
      console.log('🔧 DEBUG: No highlights found, creating fake highlights for testing');
      // Create fake highlights at the beginning of the document
      highlightedChunks.forEach((chunk, index) => {
        const start = index * 200; // Space them out
        const end = Math.min(start + 150, renderedContent.length);
        if (start < renderedContent.length) {
          highlights.push({
            start,
            end,
            index,
            chunk
          });
        }
      });
    }

    // Build JSX with highlights
    const elements: JSX.Element[] = [];
    let lastIndex = 0;

    highlights.forEach(({ start, end, index, chunk }) => {
      // Add text before highlight
      if (start > lastIndex) {
        elements.push(
          <span key={`text-${lastIndex}`}>
            {renderedContent.slice(lastIndex, start)}
          </span>
        );
      }

      // Add highlighted chunk
      elements.push(
        <span
          key={`highlight-${index}`}
          ref={(el) => (highlightRefs.current[index] = el)}
          className={`
            relative inline-block px-2 py-1 mx-1 rounded-md transition-all duration-200
            ${currentHighlight === index 
              ? 'bg-blue-200 border-2 border-blue-600 shadow-lg' 
              : 'bg-yellow-200 border-2 border-yellow-500'
            }
            hover:bg-blue-100 cursor-pointer
          `}
          onClick={() => scrollToHighlight(index)}
          title={`Chunk from page ${chunk.page} (${chunk.section}) - Similarity: ${(chunk.similarity * 100).toFixed(1)}%`}
        >
          {renderedContent.slice(start, end)}
          <span className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs px-1 rounded-full">
            {index + 1}
          </span>
        </span>
      );

      lastIndex = end;
    });

    // Add remaining text
    if (lastIndex < renderedContent.length) {
      elements.push(
        <span key={`text-${lastIndex}`}>
          {renderedContent.slice(lastIndex)}
        </span>
      );
    }

    return <div className="whitespace-pre-wrap text-sm text-gray-800 leading-relaxed">{elements}</div>;
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-20 z-40"
        onClick={onClose}
      />
      
      {/* Panel */}
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 20, stiffness: 100 }}
        className={`fixed right-0 top-0 h-full w-1/2 bg-white shadow-2xl border-l border-gray-200 z-50 flex flex-col ${className}`}
      >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold text-gray-900 truncate">
              {documentName}
            </h3>
            {highlightedChunks.length > 0 && (
              <p className="text-sm text-gray-500">
                {highlightedChunks.length} highlighted chunk{highlightedChunks.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        </div>

        {/* Navigation Controls */}
        {highlightedChunks.length > 1 && (
          <div className="flex items-center space-x-2 mr-4">
            <button
              onClick={() => scrollToHighlight(Math.max(0, currentHighlight - 1))}
              disabled={currentHighlight === 0}
              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Previous highlight"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs text-gray-500 px-2">
              {currentHighlight + 1} / {highlightedChunks.length}
            </span>
            <button
              onClick={() => scrollToHighlight(Math.min(highlightedChunks.length - 1, currentHighlight + 1))}
              disabled={currentHighlight === highlightedChunks.length - 1}
              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Next highlight"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        <button
          onClick={onClose}
          className="p-2 text-gray-600 hover:text-gray-800 hover:bg-red-100 rounded-lg transition-colors border border-gray-300 hover:border-red-300"
          title="Close document viewer"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {loading && (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading document...</span>
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center h-full p-8">
            <div className="text-center">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-red-600 mb-2">Failed to load document</p>
              <p className="text-gray-500 text-sm mb-4">{error}</p>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={loadDocumentContent}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </motion.button>
            </div>
          </div>
        )}

        {content && !loading && !error && (
          <div 
            ref={contentRef}
            className="h-full overflow-y-auto p-6 bg-white"
          >
            <div className="max-w-none">
              {renderContentWithHighlights()}
            </div>
          </div>
        )}
      </div>

      {/* Footer with chunk info */}
      {highlightedChunks.length > 0 && !loading && !error && (
        <div className="border-t border-gray-200 bg-gray-50 p-3">
          <div className="text-xs text-gray-600">
            <span className="font-medium">Legend:</span>
            <span className="ml-2 inline-block w-3 h-3 bg-blue-200 border-2 border-blue-600 rounded mr-1"></span>
            <span className="mr-4">Current highlight</span>
            <span className="inline-block w-3 h-3 bg-yellow-200 border-2 border-yellow-500 rounded mr-1"></span>
            <span>Other highlights</span>
          </div>
        </div>
      )}
    </motion.div>
    </>
  );
};

export default DocumentViewerPanel;
